import * as React from 'react';
import { useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button } from '@coursera/cds-core';

import type { Course } from 'bundles/clips/types/vlpSharedTypes';
import useEnrollIntoCourseMutation from 'bundles/clips/utils/useEnrollIntoCourseMutation';
import { getCourseHomeLinkBySlug } from 'bundles/enterprise-legacy-learner-home/utils/courseAndS12nUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/clips';

export type Props = {
  enrollText: string;
  goToCourseText: string;
  justEnrolledThroughMutation?: boolean;
  userEnrolledIntoCourse?: boolean;
  course?: Course;
  itemId?: string;
  programId?: string;
  setShowEnrollmentButtonError: (error: boolean) => void;
};

export const EnrollButton = ({
  enrollText,
  goToCourseText,
  userEnrolledIntoCourse,
  justEnrolledThroughMutation,
  course,
  itemId,
  programId,
  setShowEnrollmentButtonError,
}: Props) => {
  const trackComponent = useRetracked();
  const [isEnrolledThroughButton, setIsEnrolledThroughButton] = useState(false);

  const [enrollIntoCourseMutation, { loading: isEnrolling }] = useEnrollIntoCourseMutation();

  const isEnrolledInCourse = userEnrolledIntoCourse || justEnrolledThroughMutation || isEnrolledThroughButton;
  const buttonCopy = isEnrolledInCourse ? goToCourseText : enrollText;
  const buttonVariant = isEnrolledInCourse ? 'secondary' : 'primary';
  const ariaLabel = isEnrolledInCourse
    ? _t('#{goToCourseText}: #{courseName}', { goToCourseText, courseName: course?.name })
    : _t('#{enrollText}: #{courseName}', { enrollText, courseName: course?.name });
  const trackingName = isEnrolledInCourse ? 'sfc_vlp_go_to_course_button' : 'short_form_content_vlp_try_course_button';

  const handleClick = () => {
    if (isEnrolledInCourse && course) {
      window.open(getCourseHomeLinkBySlug(course?.slug), '_blank');
    } else {
      const input = { courseId: course?.id || '', itemId: itemId || '', programId: programId || '' };

      enrollIntoCourseMutation({ variables: input })
        .then((response) => {
          if (response?.data?.ShortFormContent_enrollEnterpriseUserIntoCourse?.success === true) {
            setIsEnrolledThroughButton(true);
            trackComponent({
              trackingData: {},
              trackingName: 'short_form_content_vlp_try_course_button',
              action: 'enrolled',
            });
          }
        })
        .catch(() => {
          setShowEnrollmentButtonError?.(true);
        });
    }
  };

  return (
    <Button
      variant={buttonVariant}
      aria-label={ariaLabel}
      loading={isEnrolling}
      onClick={handleClick}
      component={TrackedA}
      withVisibilityTracking
      trackingName={trackingName}
    >
      {buttonCopy}
    </Button>
  );
};
